import * as React from 'react';
import { useFilterSearchParams } from 'pages/Shipments/Containers/utilities';
import { DEFAULT_DATE_RANGE_PARAM } from 'pages/Shipments/params';
import { Checkbox } from 'components/tailwind-ui/Checkbox';
import { parseDateRange } from 'pages/TrackAndTrace/components/controls/filters/parsers/common';
import { Section } from './Section';
import { DateRangeOptions, Date } from './DateRangeOptions';

interface FilterStates {
  [key: string]: boolean | Date;
  moreThanTwoDays: boolean;
  lessThanTwoDays: boolean;
  startDate: Date;
  endDate: Date;
}

const defaultFilterStates: FilterStates = {
  moreThanTwoDays: false,
  lessThanTwoDays: false,
  startDate: null,
  endDate: null,
};

export const PerDiemLfd: React.FC = () => {
  const { searchParams, setSearchParams } = useFilterSearchParams(true);

  const filterStates: FilterStates = React.useMemo(() => {
    const { estPerDiemLfd } = searchParams;

    if (!estPerDiemLfd) {
      return { ...defaultFilterStates };
    }

    const range = parseDateRange(estPerDiemLfd);

    return {
      moreThanTwoDays: estPerDiemLfd.startDate === '+2:days',
      lessThanTwoDays: estPerDiemLfd.endDate === '+2:days' && estPerDiemLfd.includeEndDate,
      startDate: range?.startDate || null,
      endDate: range?.endDate || null,
    };
  }, [searchParams]);

  const clearFilters = React.useCallback(() => {
    setSearchParams({
      estPerDiemLfd: DEFAULT_DATE_RANGE_PARAM,
    });
  }, [setSearchParams]);

  const handleChange = React.useCallback(
    (v: string) => {
      if (v === 'moreThanTwoDays') {
        if (filterStates.moreThanTwoDays) {
          clearFilters();
        } else {
          setSearchParams({
            estPerDiemLfd: {
              ...DEFAULT_DATE_RANGE_PARAM,
              startDate: '+2:days',
            },
          });
        }
      } else if (v === 'lessThanTwoDays') {
        if (filterStates.lessThanTwoDays) {
          clearFilters();
        } else {
          setSearchParams({
            estPerDiemLfd: {
              ...DEFAULT_DATE_RANGE_PARAM,
              endDate: '+2:days',
              includeEndDate: true,
            },
          });
        }
      } else {
        clearFilters();
      }
    },
    [setSearchParams, clearFilters, filterStates]
  );

  return (
    <Section title="Per diem last free day filters">
      <div className="flex">
        <Checkbox
          key="moreThanTwoDays"
          id="per-diem-lfd-more-than-two-days"
          label="More than 2 days"
          checked={filterStates.moreThanTwoDays}
          onCheck={() => handleChange('moreThanTwoDays')}
          className="mr-8"
        />
        <Checkbox
          key="lessThanTwoDays"
          id="per-diem-lfd-less-than-two-days"
          label="Less than or equal to 2 days"
          checked={filterStates.lessThanTwoDays}
          onCheck={() => handleChange('lessThanTwoDays')}
          className="mr-8"
        />
        <DateRangeOptions filterStates={filterStates} field="estPerDiemLfd" />
      </div>
    </Section>
  );
};
PerDiemLfd.displayName = 'Filters.More.PerDiemLfd';
